import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { formationAPI } from '../../services/api';

// ═══════════════════════════════════════════════════════════
// 天然裂缝走向玫瑰图 + 倾角分布 + 逼近角分析
//
// 逼近角 = 天然裂缝走向与最大水平主应力(SHmax)方向的夹角, 折到 [0,90]。
// 工程含义: 逼近角小 → 水力缝易沿天然缝转向, 形成复杂缝网;
//           逼近角大 → 水力缝倾向于直接穿过天然缝, 缝网相对简单。
// ═══════════════════════════════════════════════════════════

const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  border: '#D6DAE0', panelBg: '#F4F6F9', accent: '#C2533A', blue: '#2874A6',
};

type Stats = {
  count: number;
  rose_bins: number;
  rose: number[];
  dip_hist: number[];
  md_range: [number, number];
  approach: null | {
    shmax_azimuth: number;
    mean_deg: number;
    hist_10deg: number[];
    low_angle_ratio: number;
  };
};

const CJK = '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif';

function RoseChart({ rose, bins }: { rose: number[]; bins: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr;
    const ctx = cv.getContext('2d')!;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const cx = W / 2, cy = H / 2, R = Math.min(W, H) / 2 - 34;
    ctx.clearRect(0, 0, W, H);

    const maxV = Math.max(1, ...rose);
    const step = (2 * Math.PI) / bins;

    // 同心圆刻度
    ctx.strokeStyle = 'rgba(30,42,58,0.12)';
    ctx.lineWidth = 0.8;
    [0.25, 0.5, 0.75, 1].forEach((f) => {
      ctx.beginPath(); ctx.arc(cx, cy, R * f, 0, Math.PI * 2); ctx.stroke();
    });
    // 方位刻度线 + 标注 (地质约定: 正上为北, 顺时针为方位角增大)
    ctx.strokeStyle = 'rgba(30,42,58,0.16)';
    ctx.fillStyle = TH.textSec;
    ctx.font = `11px ${CJK}`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    [['N', 0], ['E', 90], ['S', 180], ['W', 270]].forEach(([lab, deg]) => {
      const a = ((deg as number) - 90) * Math.PI / 180;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
      ctx.stroke();
      ctx.fillText(String(lab), cx + Math.cos(a) * (R + 16), cy + Math.sin(a) * (R + 16));
    });

    // 花瓣
    for (let i = 0; i < bins; i++) {
      if (!rose[i]) continue;
      const r = (rose[i] / maxV) * R;
      const a0 = (i * 360 / bins - 90) * Math.PI / 180;
      const a1 = ((i + 1) * 360 / bins - 90) * Math.PI / 180;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, a0, a1);
      ctx.closePath();
      ctx.fillStyle = 'rgba(194,83,58,0.55)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(142,58,30,0.9)';
      ctx.lineWidth = 0.7;
      ctx.stroke();
    }

    ctx.fillStyle = TH.textMuted;
    ctx.font = `10px ${CJK}`;
    ctx.textAlign = 'left';
    ctx.fillText(`最大频数 ${maxV}`, 6, 12);
  }, [rose, bins]);
  return <canvas ref={ref} style={{ width: '100%', height: 340, display: 'block' }} />;
}

function BarChart({ data, labels, color, unit }: {
  data: number[]; labels: string[]; color: string; unit: string;
}) {
  const max = Math.max(1, ...data);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 170, padding: '0 4px' }}>
      {data.map((v, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <span style={{ fontSize: 10, color: TH.textSec, fontFamily: 'monospace' }}>{v || ''}</span>
          <div style={{
            width: '100%', height: `${(v / max) * 120}px`, minHeight: v ? 2 : 0,
            background: color, opacity: 0.75, borderRadius: '2px 2px 0 0',
          }} />
          <span style={{ fontSize: 9, color: TH.textMuted, whiteSpace: 'nowrap' }}>{labels[i]}</span>
        </div>
      ))}
      <span style={{ fontSize: 10, color: TH.textMuted, alignSelf: 'flex-start' }}>{unit}</span>
    </div>
  );
}

export default function FracRosePage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [shmax, setShmax] = useState<string>('');

  const load = async (sh?: string) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const opts: any = {};
      const v = sh !== undefined ? sh : shmax;
      if (v !== '' && !Number.isNaN(Number(v))) opts.shmax = Number(v);
      const { data } = await formationAPI.fractureStats(projectId, opts);
      setStats(data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 注: 岩石力学模块目前未存储 SHmax 方位角, 故此处由用户填写。
  // 待 mechanics 增加该字段后, 可改为自动带入。
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const dipLabels = useMemo(
    () => Array.from({ length: 9 }, (_, i) => `${i * 10}–${i * 10 + 10}`), []);

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  const empty = stats && stats.count === 0;

  return (
    <div style={{ padding: 20, fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: TH.text, margin: 0 }}>天然裂缝走向玫瑰图</h2>
          <div style={{ fontSize: 12, color: TH.textMuted, marginTop: 3 }}>
            {stats ? `共 ${stats.count} 条裂缝` : '加载中…'}
            {stats && stats.count > 0 && ` · 深度 ${stats.md_range[0]}–${stats.md_range[1]} m`}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: TH.textSec }}>SHmax 方位角(°)</label>
          <input
            type="number" value={shmax} placeholder="例 75"
            onChange={(e) => setShmax(e.target.value)}
            style={{ width: 84, fontSize: 12, padding: '4px 6px', borderRadius: 4, border: `1px solid ${TH.border}` }}
          />
          <button onClick={() => load()} disabled={loading}
            style={{ fontSize: 12, padding: '5px 14px', borderRadius: 4, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', border: 'none', background: TH.accent, color: '#fff' }}>
            {loading ? '计算中…' : '计算逼近角'}
          </button>
        </div>
      </div>

      {empty ? (
        <div style={{ padding: 40, textAlign: 'center', color: TH.textSec, background: TH.panelBg, borderRadius: 6, border: `1px solid ${TH.border}` }}>
          该项目暂无天然裂缝数据。<br />
          <span style={{ fontSize: 12, color: TH.textMuted }}>
            请在「裂缝参数」子页导入成像测井解释成果 (MD / 倾角 / 倾向 / 张开度)。
          </span>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(320px, 1fr)', gap: 16 }}>
          <div style={{ background: '#fff', border: `1px solid ${TH.border}`, borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: TH.textSec, marginBottom: 6 }}>
              走向玫瑰图<span style={{ fontWeight: 400, color: TH.textMuted, marginLeft: 6 }}>走向无向, 已作对称镜像</span>
            </div>
            {stats && <RoseChart rose={stats.rose} bins={stats.rose_bins} />}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#fff', border: `1px solid ${TH.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: TH.textSec, marginBottom: 8 }}>倾角分布</div>
              {stats && <BarChart data={stats.dip_hist} labels={dipLabels} color={TH.blue} unit="°" />}
            </div>

            <div style={{ background: '#fff', border: `1px solid ${TH.border}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: TH.textSec, marginBottom: 8 }}>
                逼近角分布<span style={{ fontWeight: 400, color: TH.textMuted, marginLeft: 6 }}>裂缝走向与 SHmax 夹角</span>
              </div>
              {stats?.approach ? (
                <>
                  <BarChart data={stats.approach.hist_10deg} labels={dipLabels} color={TH.accent} unit="°" />
                  <div style={{ marginTop: 10, fontSize: 12, color: TH.textSec, lineHeight: 1.9 }}>
                    平均逼近角 <b style={{ color: TH.text }}>{stats.approach.mean_deg}°</b>
                    （SHmax {stats.approach.shmax_azimuth}°）<br />
                    逼近角 &lt;30° 的裂缝占比{' '}
                    <b style={{ color: stats.approach.low_angle_ratio > 0.4 ? TH.accent : TH.text }}>
                      {(stats.approach.low_angle_ratio * 100).toFixed(0)}%
                    </b>
                    <div style={{ fontSize: 11, color: TH.textMuted, marginTop: 4 }}>
                      占比高 → 水力缝易沿天然缝转向, 倾向形成复杂缝网；
                      占比低 → 水力缝多直接穿过, 缝网相对简单。
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ padding: '24px 8px', fontSize: 12, color: TH.textMuted }}>
                  填入最大水平主应力 SHmax 方位角后点「计算逼近角」。
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
